import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SupportWidget from '../support/SupportWidget';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['gamificacao']);
  const location = useLocation();

  const toggleMenu = (menuId: string) => {
    if (isCollapsed) {
      onToggle();
      setTimeout(() => {
        setExpandedMenus([menuId]);
      }, 300);
    } else {
      setExpandedMenus(prev => 
        prev.includes(menuId) 
          ? prev.filter(id => id !== menuId)
          : [...prev, menuId]
      );
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      title: t('sidebar.dashboard'),
      path: '/admin/incentivo/dashboard',
      icon: 'ri-dashboard-line',
      hasSubmenu: false
    },
    { 
      id: 'participantes',
      title: t('sidebar.participants'), 
      path: '/admin/incentivo/participantes',
      icon: 'ri-group-line',
      hasSubmenu: false
    },
    { 
      id: 'campanhas',
      title: t('sidebar.campaigns'), 
      path: '/admin/incentivo/campanhas',
      icon: 'ri-megaphone-line',
      hasSubmenu: false
    },
    { 
      id: 'simulador-ia',
      title: 'Simulador IA', 
      path: '/admin/incentivo/simulador-ia',
      icon: 'ri-brain-line',
      hasSubmenu: false,
      isHighlighted: true
    },
    { 
      id: 'financeiro',
      title: 'Financeiro', 
      path: '/admin/incentivo/pontuacao',
      icon: 'ri-coin-line',
      hasSubmenu: false
    },
    { 
      id: 'recompensas',
      title: t('sidebar.rewards'), 
      path: '/admin/incentivo/recompensas',
      icon: 'ri-gift-line',
      hasSubmenu: false
    },
    {
      id: 'auditoria',
      title: 'Auditoria de Varejo',
      path: '/admin/auditoria/dashboard',
      icon: 'ri-shield-check-line',
      hasSubmenu: true,
      submenu: [
        { title: 'Dashboard de Auditoria', path: '/admin/auditoria/dashboard' },
        { title: 'Vendas para Auditoria', path: '/admin/auditoria/vendas' },
        { title: 'Regras de Auditoria', path: '/admin/auditoria/regras' },
        { title: 'Trilha de Auditoria', path: '/admin/auditoria/trilha' },
        { title: 'Relatórios de Auditoria', path: '/admin/auditoria/relatorios' }
      ]
    },
    {
      id: 'gamificacao',
      title: t('sidebar.gamification'),
      path: '/admin/gamificacao/dashboard',
      icon: 'ri-gamepad-line',
      hasSubmenu: true,
      submenu: [
        { title: `${t('sidebar.dashboard')} de ${t('sidebar.gamification')}`, path: '/admin/gamificacao/dashboard' },
        { title: t('sidebar.missions'), path: '/admin/gamificacao/missoes' },
        { title: t('sidebar.xpRules'), path: '/admin/gamificacao/regras-xp' },
        { title: t('sidebar.levels'), path: '/admin/gamificacao/niveis' },
        { title: t('sidebar.badges'), path: '/admin/gamificacao/distintivos' },
        { title: t('sidebar.games'), path: '/admin/gamificacao/jogos' },
        { title: t('sidebar.logs'), path: '/admin/gamificacao/logs' },
        { title: t('sidebar.analytics'), path: '/admin/gamificacao/analytics' }
      ]
    }
  ];

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (submenu: any[]) => submenu.some(item => 
    isActive(item.path) || (item.subSubmenu && item.subSubmenu.some((sub: any) => isActive(sub.path)))
  );

  return (
    <aside className={`bg-gradient-to-b from-blue-600 via-blue-700 to-blue-800 border-r border-blue-900/30 text-white h-screen overflow-y-auto transition-all duration-300 shadow-xl flex flex-col ${isCollapsed ? 'w-16' : 'w-64'}`}>
      <div className={`p-4 border-b border-white/10 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && <h2 className="text-lg font-bold text-white">Menu Principal</h2>}
        <button
          onClick={onToggle}
          className="p-2 hover:bg-white/10 rounded-lg transition-all duration-300 cursor-pointer"
          title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-${isCollapsed ? 'menu-unfold' : 'menu-fold'}-line text-xl text-white`}></i>
          </div>
        </button>
      </div>
      
      <nav className="px-4 pt-4 flex-1">
        {menuItems.map((item) => (
          <div key={item.id} className="mb-2">
            {item.hasSubmenu ? (
              <div>
                <button
                  onClick={() => toggleMenu(item.id)}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2 rounded-lg text-left transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    isParentActive(item.submenu || []) 
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg font-bold' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }`}
                  title={isCollapsed ? item.title : ''}
                >
                  <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={item.icon}></i>
                    </div>
                    {!isCollapsed && <span className="text-sm font-bold">{item.title}</span>}
                  </div>
                  {!isCollapsed && (
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-${expandedMenus.includes(item.id) ? 'down' : 'right'}-s-line`}></i>
                    </div>
                  )}
                </button>
                
                {!isCollapsed && expandedMenus.includes(item.id) && (
                  <div className="ml-4 mt-2 space-y-1">
                    {item.submenu?.map((subItem, index) => (
                      <Link
                        key={index}
                        to={subItem.path}
                        className={`block px-3 py-2 rounded-lg text-xs transition-all duration-300 cursor-pointer whitespace-nowrap ${
                          isActive(subItem.path) 
                            ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold shadow-md' 
                            : 'text-white/80 hover:bg-white/10 hover:text-white font-semibold'
                        }`}
                      >
                        {subItem.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                to={item.path}
                className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  item.isHighlighted
                    ? isActive(item.path)
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg font-bold animate-pulse'
                      : 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md font-bold hover:from-orange-500 hover:to-orange-600 animate-pulse'
                    : isActive(item.path) 
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg font-bold' 
                      : 'text-white/90 hover:bg-white/10 hover:text-white font-semibold'
                }`}
                title={isCollapsed ? item.title : ''}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={item.icon}></i>
                </div>
                {!isCollapsed && <span className="text-sm font-bold">{item.title}</span>}
                {!isCollapsed && item.isHighlighted && (
                  <span className="ml-auto">
                    <i className="ri-sparkling-2-fill text-yellow-300 text-xs"></i>
                  </span>
                )}
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* Área de Suporte - Fixada no Bottom */}
      <div className="p-4 border-t border-white/10 mt-auto">
        {!isCollapsed ? (
          <SupportWidget />
        ) : (
          <div className="flex justify-center">
            <button
              onClick={onToggle}
              className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 cursor-pointer"
              title={`${t('sidebar.support')} - Clique para expandir`}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-customer-service-2-fill text-xl"></i>
              </div>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
